/**
 * pages/GoalCheckin.tsx — the walk: one key result at a time, in `position` order.
 *
 * Six rules govern this screen and none of them is negotiable. Each is enforced at the point named
 * below, and each has an assertion in `GoalCheckin.test.tsx` naming the edit here that turns it red —
 * rule 5 included, since the metric choice is the reason this change was asked for and should not have
 * been the one rule resting on review.
 *
 * **1. Nothing is posted that the person did not ask to post — and a preview is always available.**
 * `draft` and `post` are two calls and the per-key-result path never chains them: pressing *Preview
 * update* writes nothing to Atlassian, and the sentence, the traffic-light and the figure are on screen
 * before *Post to Atlassian* exists to press.
 *
 * ⚠️ **This rule was NARROWED on 7 August 2026, on the owner's instruction, and the narrowing is the one
 * thing on this screen a reader must not mistake for an accident.** It used to read "preview before every
 * write", with the note that a combined draft-and-post control would be the one everybody pressed. That
 * control now exists, because walking eleven key results in preview-then-post pairs is eleven pairs of
 * decisions for a person who made all their decisions in the form. *Finish check-in* collects everything
 * answered and not yet posted, **asks once**, and posts the lot.
 *
 * What survives the narrowing is what the rule was actually protecting. The confirmation is not a
 * yes/no: it lists every key result it is about to write, with its traffic-light, its confidence and its
 * reading movement — the "exact status, exact figure" half of the old rule, in full — and it says of each
 * sentence whether it has been written and read, or whether the model will write it during the post. The
 * one thing that genuinely goes is the guarantee that a human read every SENTENCE, and the dialog says so
 * in those words rather than implying otherwise. `runBatch` is the only code path that may post without a
 * preview; the edit that breaks this rule is removing the confirmation, or letting that dialog claim a
 * sentence exists where none has been written.
 *
 * **2. `checkin_indeterminate` MUST NOT offer a retry.** A goal update is additive and has no delete, so
 * a second post leaves two contradictory check-ins on a key result somebody's leadership reads — and the
 * first may have landed. The screen therefore ENDS that key result: no post, no retry, and **no re-draft
 * either**. The server now refuses all three itself — `indeterminate` is a terminal state of its own and
 * `answerCheckinItem`, `markItemPosted` and the post route each decline it — so a client that offered
 * any of them would be walking somebody into a conflict rather than protecting them from one. The person
 * is sent to Atlassian to look, which is the only thing that can actually settle it. This is the most
 * important behaviour on the screen; everything else here is recoverable.
 *
 * ⚠️ The batch honours it twice: an indeterminate key result is never enqueued, and an indeterminate
 * outcome DURING the batch stops the run where it stands rather than carrying on down the list. The
 * second half is the one worth stating — a post whose outcome is unknown is usually a connection that has
 * stopped answering, and firing four more irreversible writes into it is precisely the wrong reflex.
 *
 * **3. A mismatch on a successful post is shown.** `mismatch: true` means Atlassian recorded a different
 * traffic-light from the one intended — the single symptom of a mis-set score — and it is invisible
 * unless the screen says so.
 *
 * **4. The identity that posts is not the person.** `connection.postedAs` is the business unit's own
 * Atlassian account, and Atlassian's history will name it while this application's audit trail names
 * them — so the person must be able to see it. It is named in the toast confirming each successful post
 * ("Posted as ticketless@centro.team."), on every posted key result beside the time it landed, and in the
 * batch's own confirmation before it writes anything.
 *
 * ⚠️ It is NOT stated in a standing banner above the screen any more; the owner removed that on
 * 7 August 2026. The rule survives the removal because the banner was the least useful of the three
 * sites — it appeared before anything had been posted, said the same thing on every render, and was read
 * once. The edit that would actually break rule 4 is dropping the account from the TOAST or from the
 * posted line, which is where somebody is checking what just happened rather than scrolling past.
 *
 * **5. The metric is CHOSEN, never inferred from a blank box.** Every key result is configured
 * differently — one tracks a percentage, the next a count from a start to an end, the next no number at
 * all — and the reading is the figure leadership actually looks at. This screen once offered a bare box
 * whose hint said "leave blank to keep", which asks a person to know that an empty field is a decision;
 * the ones who did not know left it blank meaning "I have not got to that yet" and got the same silence.
 *
 * It is now ONE field **seeded with the reading as it stands**, and the two numbers on this form are
 * seeded on the same principle: the confidence starts at the band's midpoint, which is the value that
 * would have been applied had it been left empty. A default a person can read is a default they can
 * disagree with; a default hidden in a hint is one they have to be told about.
 *
 * ⚠️ **The edit that breaks this is seeding the box with `formatMetricValue`'s rendering rather than the
 * raw number** — "20%" and "1,200" look right and do not parse back — **or dropping the
 * `typed !== seedMetricText(item)` comparison in `buildDraftInput`**, which is what keeps an untouched
 * field from posting a metric movement that nobody made. An intermediate design used two radios (*Keep it
 * at 20%* / *Set a new value*); it solved the same problem and the owner replaced it on 7 August 2026,
 * because a box that is never blank cannot be misread as a blank one either, and it costs no click.
 *
 * Since the metric now travels INSIDE the update (`metricUpdate` on the one mutation) rather than as a
 * separate write before it, a reading can no longer land on its own with no check-in beside it: there is
 * one write, or none. The warning that it OVERWRITES the reading still stands — the update appends, the
 * number does not.
 *
 * **6. What a person typed is theirs until they post it or leave.** Every field on this form is held in
 * an `answers` map keyed by the KEY RESULT, not in one set of variables re-seeded as the walk moves. That
 * is the whole of the mechanism, and it is the fix for the defect the owner reported on 7 August 2026:
 * moving to the next key result and coming back used to discard everything typed but not drafted, so
 * anybody working through eleven of them had to press *Preview update* on each as a save button.
 *
 * ⚠️ **The map, and only the map, is the form's state.** There is deliberately no effect copying an
 * item's recorded answers into component state on arrival — that shape re-seeds on every dependency it
 * cannot see, and the version of it that lived here had to be keyed on `currentId` by hand to stop the
 * previous card's numbers appearing under the next one. `answerFor` derives instead: a stored answer if
 * the person has touched this key result, otherwise the seed computed from the item. Nothing to
 * synchronise, and the first paint is already right.
 *
 * `POST /goals/:id/checkins` is issued on arrival and is the same call whether one is open or not: the
 * server hands back the open one with `resumed: true` rather than opening a second, so arriving here IS
 * resuming. One check-in runs on an objective at a time, deliberately.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  closeCheckin,
  draftCheckinItem,
  goalErrorMessage,
  postCheckinItem,
  skipCheckinItem,
  startCheckin,
  type CheckinView,
  type GoalCheckinState,
  type ItemView,
  type PostItemResponse,
  type StartCheckinResponse,
} from "../lib/api";
import { formatDateTime, formatRelative } from "../lib/format";
import { ScrollStrip } from "../components/ScrollStrip";
import {
  CHECKIN_STATE_OPTIONS,
  carriedMetricKind,
  describeMetric,
  firstUnsettled,
  formatMetricValue,
  goalStateLabel,
  goalStateTone,
  hasMismatch,
  inPositionOrder,
  isIndeterminate,
  isSettled,
  NOT_READ,
  orNotRead,
  readScoreDecimal,
  scoreAsDecimal,
  scoreBandHint,
  scoreBandRange,
  scoreBandsOf,
  type ScoreBands,
  useAsync,
  type CheckinDraftInput,
  type MetricKind,
} from "./goalsShared";
import {
  Badge,
  Banner,
  Button,
  Card,
  DeniedState,
  ErrorState,
  FormActions,
  LoadingState,
  Modal,
  Page,
  RadioGroup,
  Spinner,
  TextArea,
  TextField,
  useToast,
  type Tone,
} from "../components/ui";

/** `server/goals/summary.ts`'s `SUMMARY_MAX_CHARS`. Atlassian's own limit on a check-in sentence. */
const SUMMARY_MAX_CHARS = 280;

/**
 * `DraftSchema.note`'s own bound in `server/routes/goals.ts`.
 *
 * Passed to the control so the browser stops rather than the round trip, and deliberately never shown:
 * it is a safety bound on text that gets relayed into a model prompt, not a length anybody writing a
 * note is expected to think about. The 280 that a person DOES meet is the verbatim one, and only the
 * verbatim reading of this box is counted against it.
 */
const NOTE_MAX_CHARS = 10_000;

/** The card the walk is currently on. Its `<h2>` is what a move scrolls to. */
const CURRENT_CARD_ID = "checkin-current-key-result";

export interface GoalCheckinPorts {
  readonly start: () => Promise<StartCheckinResponse>;
  readonly draft: (checkinId: string, itemId: string, input: CheckinDraftInput) => Promise<ItemView>;
  readonly post: (checkinId: string, itemId: string) => Promise<PostItemResponse>;
  readonly skip: (checkinId: string, itemId: string) => Promise<ItemView>;
  readonly close: (checkinId: string, status: "completed" | "abandoned") => Promise<CheckinView>;
}

export interface GoalCheckinProps {
  readonly buId: string;
  readonly goalId: string;
  readonly ports?: GoalCheckinPorts;
  /** Where to go once the check-in is closed. Defaults to the objective's record page. */
  readonly onFinished?: () => void;
}

function asApiError(cause: unknown): ApiError {
  return cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null);
}

/** Drop one key from a refusal map. Written out because `delete` on a readonly record needs a copy. */
function without(
  map: Readonly<Record<string, ApiError>>,
  key: string,
): Readonly<Record<string, ApiError>> {
  const next = { ...map };
  delete next[key];
  return next;
}

/** Add to / remove from a readonly set without mutating the one React is holding. */
function withMember(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  return set.has(id) ? set : new Set(set).add(id);
}

function withoutMember(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  if (!set.has(id)) return set;
  const next = new Set(set);
  next.delete(id);
  return next;
}

/**
 * The word on a key result's badge.
 *
 * `blocked` outranks the stored state because it IS its state now — a post refused as indeterminate a
 * moment ago has not been re-read, so the copy in hand still says `drafted`. The explicit
 * `indeterminate` case below is not therefore redundant: it is what a RELOADED item reads as, and it
 * means the badge tells the truth even if `blockedFromPosting` were ever broken.
 */
export function stateWord(item: ItemView, blocked: boolean): string {
  if (blocked) return "Outcome unknown";
  switch (item.state) {
    case "posted":
      return "Posted";
    case "skipped":
      return "Skipped";
    case "drafted":
      return "Drafted, not sent";
    case "failed":
      return "Not sent";
    case "indeterminate":
      return "Outcome unknown";
    default:
      return "To answer";
  }
}

/**
 * The five marks a pager step can carry — which are NOT the six `stateWord` answers, deliberately.
 *
 * A row of seven numerals is glanced at, not read. The question it has to answer at a glance is *how far
 * have I got, is anything waiting to go, and is anything wrong*, and six shades cannot answer that faster
 * than four marks can. The precise word survives in each button's accessible name, where a reader who
 * wants it can get it, and on the card itself once they arrive.
 *
 * ⚠️ **`posted` is its own mark rather than part of a general "settled", on the owner's instruction of
 * 7 August 2026, and the distinction it draws is the one this screen exists for.** A filled dot means
 * *there are answers here and they have not gone anywhere*; a tick means *this is in Atlassian and cannot
 * be taken back*. Folding the two into one shade — which is how this shipped — told a person walking a
 * long objective that eleven key results were "done" when only four had actually been written anywhere.
 *
 * `skipped` gets a bar rather than a dot for the same reason: it is settled and it is deliberately empty,
 * which no amount of fill or hollowness can say. And `blocked` — the item whose post may already have
 * landed, rule 2 — is `attention`, never `posted`, because "we do not know" is exactly the thing a reader
 * must not skim past.
 */
export type PagerMark = "posted" | "attention" | "entered" | "skipped" | "open";

/**
 * ⚠️ The order of these tests is the whole of the function, and two of them are load-bearing.
 *
 * `attention` is taken BEFORE `awaiting`, so a key result whose post was refused reads as needing another
 * look rather than as merely answered — it has answers, and that is not the interesting thing about it.
 * `awaiting` is taken before `skipped`, so a key result somebody skipped and then answered after all
 * reads as answered; the bar means "settled and deliberately empty", which it no longer is.
 */
export function pagerMark(
  item: ItemView,
  blocked: boolean,
  awaiting: boolean,
  refused: boolean,
): PagerMark {
  if (blocked) return "attention";
  if (item.state === "posted") return "posted";
  // ⚠️ `refused` is the SESSION's copy of a refusal, and it is not covered by the two states beside it.
  // A refused post returns an `ApiError`, never an item, so the copy in hand still reads `drafted` — and
  // without this the numeral for a key result whose post was just refused fell through to `entered`, or
  // (while a final refusal excluded it from `awaiting`) all the way to `open`, which says nobody has
  // touched it. A row of numerals is glanced at; the one thing it must never do is understate.
  if (refused || item.state === "failed" || item.state === "indeterminate") return "attention";
  if (awaiting) return "entered";
  if (item.state === "skipped") return "skipped";
  return "open";
}

/**
 * How this key result's figures are written — the server's answer, never re-derived from `metric.type`.
 *
 * Wrapped at one line so that every site on this screen asks the same question the same way; a key result
 * that tracks no number answers `number`, which nothing then uses, because neither control is drawn.
 */
function kindOf(item: ItemView): MetricKind {
  return carriedMetricKind(item.metric);
}

/** Has a sentence and a traffic-light been recorded for this key result? Then it can be posted as it is. */
function isDrafted(item: ItemView): boolean {
  return item.summary !== null && item.newState !== null;
}

/**
 * ⚠️ **Is there anything here that has not been posted?** — the question both of the owner's 7 August
 * asks turn on, so it is answered once.
 *
 * It feeds the pager's filled dot and it decides which key results *Finish check-in* offers to send. Two
 * sources, because "entered" has two meanings that must not be allowed to disagree:
 *
 *   - `edited` — this session's own record of a field being touched. It catches the person who typed a
 *     note thirty seconds ago and has drafted nothing, which no column on the item can see.
 *   - the ITEM's own recorded answers — a state, a score, a reading, a note or a sentence. It catches the
 *     work of a session that has since been closed, which the set above cannot see, on a check-in resumed
 *     the next morning.
 *
 * ⚠️ Only ever asked of a key result that is NOT settled. Every one of these columns is populated on a
 * posted item, so asking it there would answer "yes" for the whole list.
 */
function hasEntry(item: ItemView, edited: ReadonlySet<string>): boolean {
  if (edited.has(item.id)) return true;
  return (
    item.newState !== null ||
    item.newScore !== null ||
    item.newMetricValue !== null ||
    item.note !== null ||
    item.summary !== null
  );
}

/**
 * ⚠️ **Would finishing offer to post this?** — ONE definition, and it must stay one.
 *
 * The pager's filled dot and the batch's list are the same claim made in two places: the numeral says
 * *there is something here that has not gone*, and the dialog then says what. Two functions answering
 * that would eventually disagree, and the shape of the disagreement is a person seeing six filled dots
 * and being offered five updates — with no way to find out which one was dropped.
 *
 * ⚠️ **A skip is a decision and it STANDS.** It is taken back by answering the key result again — the
 * *Answer it after all* button — and never by pressing Finish, so a skipped key result is in play only
 * once somebody has actually touched it in this session. Without that clause the first draft of this
 * function excluded EVERY skipped item outright, which quietly dropped exactly the work of the person
 * who skipped one, thought better of it, typed an answer, and pressed Finish.
 *
 * `posted` and `indeterminate` are out for opposite reasons: one is done, and one may be (rule 2).
 */
function awaitingPost(item: ItemView, edited: ReadonlySet<string>, blocked: boolean): boolean {
  if (blocked) return false;
  if (item.state === "posted" || item.state === "indeterminate") return false;
  if (item.state === "skipped") return edited.has(item.id);
  return hasEntry(item, edited);
}

/**
 * What the reading field starts with: the number exactly as Atlassian holds it.
 *
 * ⚠️ THE RAW NUMBER, NOT `formatMetricValue`'s rendering of it. The field is an input, and what comes out
 * of it is parsed straight back — so seeding it with "20%" or "1,200" would mean a person who changed
 * nothing submitted something unparseable, and one who edited a digit submitted a thousands separator.
 * The unit belongs in the label and the hint, which is where it is.
 *
 * Empty when the reading could not be read. That is a real state and distinct from "tracks no number":
 * there IS a metric here and the sync did not bring its value back, so there is nothing honest to seed.
 * The person may still type one, and blank still means "leave it alone".
 */
function seedMetricText(item: ItemView): string {
  if (item.metric === null || item.metric.current === null) return "";
  return String(item.metric.current);
}

/**
 * What the confidence field starts with: the value that WOULD have been used had it been left blank.
 *
 * The owner's instruction, and it is the better default for a reason worth stating — the midpoint was
 * always going to be applied, so a blank box was not offering a choice, it was hiding one. Seeded, the
 * number is on screen where it can be disagreed with. Blank remains valid and still means the midpoint,
 * so nothing depends on the seed surviving.
 *
 * Empty where the server did not state its bands, which is the same case in which the field is not drawn.
 */
function seedScoreText(state: GoalCheckinState, bands: ScoreBands | null): string {
  const band = bands?.[state];
  return band === undefined ? "" : scoreAsDecimal(band.midpoint);
}

/**
 * Everything the form holds for ONE key result — rule 6.
 *
 * Held as text where the person types text, including both numbers. Holding the confidence as a number
 * would force a parse on every keystroke and would have to decide what "0." means, which is nothing yet;
 * it is converted once, when the draft is built, and converted back once, when this is seeded.
 */
interface Answer {
  readonly state: GoalCheckinState;
  readonly scoreText: string;
  readonly metricText: string;
  readonly wordsMode: "notes" | "verbatim";
  readonly words: string;
}

/**
 * The form as it opens on a key result nobody has touched this session.
 *
 * ⚠️ This is where a RESUMED check-in is re-hydrated as well: a person who answered three key results
 * yesterday and came back today must find their own answers, not a fresh set of defaults. Where nothing
 * has been answered, both numbers are SEEDED rather than left blank, and the two seeds are different in
 * kind — the confidence with the value that would be applied anyway (the band's midpoint, so the field
 * states the default instead of hiding it in a hint), the reading with the number as Atlassian holds it,
 * so that leaving it alone is visibly leaving it alone.
 *
 * ⚠️ Only a sentence the PERSON wrote comes back as `verbatim`. A GENERATED summary is deliberately not
 * restored into the box: doing so would silently turn every re-draft into "use this verbatim", and the
 * model would never write again for that key result.
 */
function seedAnswer(item: ItemView, bands: ScoreBands | null): Answer {
  const state = item.newState ?? "on_track";
  const ownSentence = item.summarySource === "user" ? (item.summary ?? "") : "";
  return {
    state,
    scoreText: item.newScore === null ? seedScoreText(state, bands) : scoreAsDecimal(item.newScore),
    metricText: item.newMetricValue === null ? seedMetricText(item) : String(item.newMetricValue),
    wordsMode: ownSentence === "" ? "notes" : "verbatim",
    words: ownSentence === "" ? (item.note ?? "") : ownSentence,
  };
}

/** The three things a form can be wrong about, kept apart so each lands under its own control. */
interface FieldErrors {
  readonly score?: string;
  readonly metric?: string;
  readonly words?: string;
}

const NO_ERRORS: FieldErrors = {};

function firstProblem(errors: FieldErrors): string | null {
  return errors.score ?? errors.metric ?? errors.words ?? null;
}

type DraftAttempt =
  | { readonly ok: true; readonly input: CheckinDraftInput }
  | { readonly ok: false; readonly errors: FieldErrors };

/**
 * The answers as a draft request, or every reason they cannot be one.
 *
 * ⚠️ **A pure function, and the ONE place the form is read.** It is called from two very different
 * places — the *Preview update* button, which shows what it refuses under the offending field, and the
 * batch, which must know BEFORE it starts whether every key result it is about to write is valid. A
 * second copy of these rules for the batch would be a second answer to "is this confidence in band",
 * and the two would disagree on the day somebody changed one.
 *
 * ⚠️ Every problem is collected, never returned on the first. A form that surrenders one at a time makes
 * a person press the button, read a sentence, fix a field and press again for each thing they got wrong —
 * and this is the button they are being taught to press carefully.
 */
function buildDraftInput(item: ItemView, answer: Answer, bands: ScoreBands | null): DraftAttempt {
  const errors: { score?: string; metric?: string; words?: string } = {};

  /* The confidence. A blank box is a valid answer and means the band's midpoint: the server resolves it
   * (`resolveScore`) and nothing is sent. */
  let score: number | undefined;
  if (bands !== null) {
    const band = bands[answer.state];
    const reading = readScoreDecimal(answer.scoreText, band);
    switch (reading.outcome) {
      case "blank":
        break;
      case "score":
        score = reading.score;
        break;
      case "not_a_number":
        errors.score = `Enter a decimal between ${scoreAsDecimal(band.min)} and ${scoreAsDecimal(band.max)}, or leave it blank for ${scoreAsDecimal(band.midpoint)}.`;
        break;
      case "too_fine":
        errors.score = "Two decimal places at most — Atlassian records the confidence in hundredths.";
        break;
      case "outside_band":
        // ⚠️ The score IS the traffic-light on this gateway. A value outside the band would post a status
        // other than the one on screen, in a record that cannot be corrected, so the sentence says what
        // that costs rather than merely naming the bound.
        errors.score = `${scoreBandHint(answer.state, band)} A confidence outside that band would post a different status from the one chosen here.`;
        break;
    }
  }

  /* The metric. Only where the key result HAS one: sending a reading where there is none is
   * `checkin_no_metric`, and neither control is drawn in that case anyway. */
  const typed = answer.metricText.trim();
  let newMetricValue: number | undefined;
  if (item.metric !== null && typed !== "" && typed !== seedMetricText(item)) {
    // ⚠️ `typed !== seedMetricText(item)` IS THE "KEEP IT" DECISION, and it is why the field can be
    // seeded without turning every check-in into a metric write. A person who leaves the box exactly as
    // they found it has changed nothing, so nothing travels — no `metricUpdate` in the mutation, and
    // Atlassian's update carries no "Progress changed" block claiming a movement that did not happen.
    //
    // Compared as TEXT rather than as parsed numbers, deliberately. "20" and "20.0" are the same number
    // and are not the same answer: typing the second is someone deciding to state the reading, and the
    // round trip that follows is harmless. The failure that matters runs the other way — treating a
    // deliberate edit as a no-op — and text comparison cannot produce it.
    //
    // A blank box still means "leave it alone", which is what it meant before the field was seeded.
    if (!Number.isFinite(Number(typed))) {
      errors.metric =
        kindOf(item) === "percentage"
          ? "Enter a number — just the figure, without the per-cent sign."
          : "Enter a number.";
    } else {
      newMetricValue = Number(typed);
    }
  }

  /* The words, and which of the two things they are. Only the verbatim reading is counted: it IS the
   * check-in, and Atlassian refuses it past 280. Notes are raw material and are bounded only by the
   * server's safety limit, which nobody drafting a note will ever meet. */
  const said = answer.words.trim();
  if (answer.wordsMode === "verbatim" && said.length > SUMMARY_MAX_CHARS) {
    // Reachable despite the control's `maxLength`, and that is exactly why the check is here: `maxLength`
    // stops TYPING past the bound, it does not shorten a value already in the box. Someone who wrote four
    // paragraphs of notes and then chose "post exactly what I write" is over it without having typed a
    // character.
    errors.words = `That is ${said.length - SUMMARY_MAX_CHARS} character(s) too long to post as written.`;
  }

  if (errors.score !== undefined || errors.metric !== undefined || errors.words !== undefined) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    input: {
      state: answer.state,
      ...(score === undefined ? {} : { score }),
      ...(newMetricValue === undefined ? {} : { metricValue: newMetricValue }),
      // ⚠️ One field, one destination — never both. `summary` present means "use this verbatim" all the
      // way down (`summary_source = 'user'`), so sending the same text as a note beside it would ask the
      // model to write from the sentence it is being told not to replace.
      ...(said === "" ? {} : answer.wordsMode === "verbatim" ? { summary: said } : { note: said }),
    },
  };
}

/** Where one key result has got to inside a batch. */
type StepPhase = "waiting" | "writing" | "posting" | "posted" | "failed" | "cannot";

interface BatchStep {
  readonly itemId: string;
  readonly key: string;
  readonly name: string;
  readonly phase: StepPhase;
  /** What went wrong, or what Atlassian did that the person should know about. */
  readonly detail: string | null;
}

interface Batch {
  readonly steps: readonly BatchStep[];
  readonly running: boolean;
  /** Set when a step failed: the run stops where it stands and the dialog says what did and did not go. */
  readonly halted: boolean;
}

/**
 * "5 → 3", or the sentence that says the number is not moving.
 *
 * Written as the transition Atlassian itself renders on a posted update — "Progress 0% → 20%" — rather
 * than as the new figure alone. The reading is being OVERWRITTEN, and a person approving that should see
 * what is being written over; "20%" on its own is only half the sentence.
 *
 * The unreadable-current case is named rather than rendered as "No change — it stays at Not read", and it
 * is compared on the FORMATTED text so that every shape `formatMetricValue` calls unreadable takes the
 * branch, not just `current === null`.
 */
function progressLine(entry: ItemView): string {
  if (entry.metric === null) return "This key result tracks no number";
  if (entry.newMetricValue === null) {
    return formatMetricValue(entry.metric.current, kindOf(entry)) === NOT_READ
      ? "No change — it stays as Atlassian holds it"
      : `No change — it stays at ${formatMetricValue(entry.metric.current, kindOf(entry))}`;
  }
  return `${formatMetricValue(entry.metric.current, kindOf(entry))} → ${formatMetricValue(entry.newMetricValue, kindOf(entry))}`;
}

function phaseWord(phase: StepPhase): string {
  switch (phase) {
    case "writing":
      return "Writing the update…";
    case "posting":
      return "Posting to Atlassian…";
    case "posted":
      return "Posted";
    case "failed":
      return "Not posted";
    case "cannot":
      return "Cannot be posted";
    default:
      return "Waiting";
  }
}

function stateTone(item: ItemView, blocked: boolean): Tone {
  if (blocked) return "danger";
  switch (item.state) {
    case "posted":
      return "success";
    case "skipped":
      return "neutral";
    case "drafted":
      return "info";
    case "failed":
      return "warning";
    case "indeterminate":
      return "danger";
    default:
      return "neutral";
  }
}

/** The tick, drawn rather than typed: a glyph would be the reader's font's idea of a tick, not ours. */
function Tick(): JSX.Element {
  return (
    <svg className="checkin-pager__tick" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
      <path
        d="M2 6.4 4.8 9 10 3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function GoalCheckin({ buId, goalId, ports, onFinished }: GoalCheckinProps): JSX.Element {
  const toast = useToast();

  const start = ports?.start ?? ((): Promise<StartCheckinResponse> => startCheckin(goalId));
  /**
   * ⚠️ **`draftCheckinItem` forwards `score`, and it shipped for a while not doing so.**
   *
   * `lib/api.ts` is the one HTTP seam and it assembles the draft body field by field — `score`, `state`,
   * `metricValue`, `note`, `summary` — so that `exactOptionalPropertyTypes` cannot smuggle an explicit
   * `undefined` onto the wire. The consequence is that a field missing from that list never leaves the
   * browser, however carefully this screen collects it: the check-in posts, the server applies the band
   * midpoint because no score arrived, and nothing anywhere says the person's number was discarded. That
   * is exactly what happened to `score` between this screen landing and the seam catching up, and no
   * port-level test could see it — the port had the number; the request did not.
   *
   * This screen deliberately does NOT reconstruct the request itself to get around a gap like that; a page
   * module that builds its own URL and body is a second HTTP seam, and the next one would be built by
   * copying this one. It is asserted instead, from the outside, by the test in `GoalCheckin.test.tsx` that
   * mounts this component with **no ports at all** and reads what `fetch` was handed.
   */
  const draft =
    ports?.draft ??
    ((checkinId: string, itemId: string, input: CheckinDraftInput): Promise<ItemView> =>
      draftCheckinItem(goalId, checkinId, itemId, input));
  const post =
    ports?.post ??
    ((checkinId: string, itemId: string): Promise<PostItemResponse> =>
      postCheckinItem(goalId, checkinId, itemId));
  const skip =
    ports?.skip ??
    ((checkinId: string, itemId: string): Promise<ItemView> => skipCheckinItem(goalId, checkinId, itemId));
  const closeIt =
    ports?.close ??
    ((checkinId: string, status: "completed" | "abandoned"): Promise<CheckinView> =>
      closeCheckin(goalId, checkinId, status));

  const session = useAsync(() => start(), `checkin:${buId}:${goalId}`);

  /**
   * The items are DERIVED from the response with an overrides map laid over them, rather than copied into
   * state and seeded by an effect.
   *
   * The copy-and-seed shape has a visible defect: there is one commit on which the response has landed
   * and the effect has not run, so the screen renders with zero key results — and the "this objective has
   * no key results" branch flashes on every arrival. Deriving means the first paint is already correct.
   */
  const [overrides, setOverrides] = useState<Readonly<Record<string, ItemView>>>({});
  const data = session.data;

  const items = useMemo<readonly ItemView[]>(() => {
    if (data === null) return [];
    return inPositionOrder(data.checkin.items.map((item) => overrides[item.id] ?? item));
  }, [data, overrides]);

  /**
   * `null` means "wherever the walk should open", which is the first key result still wanting an answer.
   *
   * It is pinned to a number by the effect below on the first commit that has items, and never floats
   * again — because a cursor that kept re-deriving would jump AWAY from a key result the instant it was
   * posted, taking the confirmation the person had just earned with it.
   */
  const [cursorState, setCursor] = useState<number | null>(null);
  const cursor = cursorState ?? firstUnsettled(items);

  useEffect(() => {
    if (cursorState === null && items.length > 0) setCursor(firstUnsettled(items));
  }, [cursorState, items]);

  /**
   * Two session-scoped sets, held rather than derived, for opposite reasons.
   *
   * `indeterminate` is belt to `isIndeterminate`'s braces: the refusal tells us NOW, the item's own
   * columns tell us after a reload, and neither alone covers both. `mismatched` is the server's own
   * verdict on a post, unioned with the same comparison re-derived from the item, so that a check-in
   * resumed in a later session still warns — its response was answered to a page long since closed.
   */
  const [indeterminate, setIndeterminate] = useState<ReadonlySet<string>>(new Set());
  const [mismatched, setMismatched] = useState<ReadonlySet<string>>(new Set());
  const [postedUrls, setPostedUrls] = useState<Readonly<Record<string, string>>>({});

  /** Refusals kept apart by the call that produced them: a failed draft must not disarm a post. */
  const [draftRefusals, setDraftRefusals] = useState<Readonly<Record<string, ApiError>>>({});
  const [postRefusals, setPostRefusals] = useState<Readonly<Record<string, ApiError>>>({});

  const [busy, setBusy] = useState<string | null>(null);
  const [abandoning, setAbandoning] = useState(false);
  const [batch, setBatch] = useState<Batch | null>(null);
  /** Set synchronously when a run starts, so a second press cannot begin a second one. */
  const runningRef = useRef(false);
  /** The dialog's only focusable element while a run is in flight — see the comment at its call site. */
  const progressRef = useRef<HTMLParagraphElement | null>(null);
  const running = batch?.running === true;

  /* -------------------------------------------------------------------------------------------------
   * Rule 6 — the form, held per key result
   * ----------------------------------------------------------------------------------------------- */

  /** What the person has typed, keyed by key result. Absent means "as `seedAnswer` would have it". */
  const [answers, setAnswers] = useState<Readonly<Record<string, Answer>>>({});
  /**
   * Which key results have been touched since they were last drafted.
   *
   * ⚠️ It is what stops the batch from re-writing a sentence somebody has already read. An item that is
   * drafted and unedited is posted with the sentence it was previewed with; one that has been edited
   * since is re-drafted first, because the recorded sentence no longer answers the recorded answers.
   * Cleared on a successful draft, which is the moment the two agree again.
   */
  const [edited, setEdited] = useState<ReadonlySet<string>>(new Set());
  /** Validation, per key result, so one card's refusal never appears under another's fields. */
  const [errors, setErrors] = useState<Readonly<Record<string, FieldErrors>>>({});
  /**
   * Which key results are showing their FORM rather than what is recorded for them.
   *
   * Per key result rather than one flag for the screen: pressing *Edit the answers* on the third and
   * moving to the fourth used to open the fourth's form over a preview it had already earned.
   */
  const [editing, setEditing] = useState<ReadonlySet<string>>(new Set());

  const checkin = data?.checkin ?? null;
  const connection = data?.connection ?? null;
  const current = items[cursor];

  /**
   * The score bands, straight off the response and never re-derived here (`goalsShared.ts` says why).
   *
   * `null` means this server did not state them, and the confidence field is then not drawn at all —
   * every check-in goes with the midpoint, exactly as it did before the field existed. A field hinting a
   * band this screen had guessed would refuse values the server accepts, which reads as a broken form.
   */
  const bands = useMemo(() => scoreBandsOf(data), [data]);

  const answerFor = useCallback(
    (item: ItemView): Answer => answers[item.id] ?? seedAnswer(item, bands),
    [answers, bands],
  );

  const updateAnswer = useCallback(
    (item: ItemView, change: (previous: Answer) => Answer): void => {
      setAnswers((previous) => ({
        ...previous,
        [item.id]: change(previous[item.id] ?? seedAnswer(item, bands)),
      }));
      setEdited((previous) => withMember(previous, item.id));
    },
    [bands],
  );

  const clearError = useCallback((item: ItemView, field: keyof FieldErrors): void => {
    setErrors((previous) => {
      const held = previous[item.id];
      if (held === undefined || held[field] === undefined) return previous;
      const next: { score?: string; metric?: string; words?: string } = { ...held };
      delete next[field];
      return { ...previous, [item.id]: next };
    });
  }, []);

  const replaceItem = useCallback((updated: ItemView): void => {
    setOverrides((previous) => ({ ...previous, [updated.id]: updated }));
  }, []);

  const goTo = useCallback(
    (index: number): void => {
      if (index < 0 || index >= items.length) return;
      setCursor(index);
    },
    [items.length],
  );

  /**
   * Put the new key result's heading at the top of the window when the walk moves.
   *
   * The card is long — a traffic-light, two numbers, a box and the buttons — so on any ordinary screen
   * pressing *Next key result* leaves the reader where the last one's buttons were, looking at the middle
   * of a key result they have not seen the name of. The heading is the answer to "which one am I on now",
   * so it is what gets scrolled to.
   *
   * ⚠️ NOT ON FIRST PAINT. `previousCursor` starts null and the first run only records where we are: a
   * page that scrolls itself the moment it loads fights the browser's own restoration and steals the
   * position from anyone who arrived by a link. This fires on a MOVE, which is the thing the reader did.
   *
   * `prefers-reduced-motion` is honoured because the design system's motion rules are not advisory — a
   * long smooth scroll is precisely the animation that makes some people ill.
   */
  const previousCursor = useRef<number | null>(null);
  useEffect(() => {
    const was = previousCursor.current;
    previousCursor.current = cursor;
    if (was === null || was === cursor) return;
    const heading = document.getElementById(CURRENT_CARD_ID);
    if (heading === null) return;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    heading.scrollIntoView({ behavior: still ? "auto" : "smooth", block: "start" });
  }, [cursor]);

  /**
   * Put focus on the progress line when a run begins.
   *
   * The button that started it has just been unmounted, so without this focus is on `<body>` — outside
   * the dialog, which is both a lost place in the reading order and the thing that lets Tab escape.
   */
  useEffect(() => {
    if (running) progressRef.current?.focus();
  }, [running]);

  /**
   * ⚠️ Rule 2. A key result whose post may already have landed is never posted again — not by this
   * session, which holds the refusal, and not by a later one, which reads it back off the item.
   */
  const blockedFromPosting = useCallback(
    (item: ItemView): boolean => indeterminate.has(item.id) || isIndeterminate(item),
    [indeterminate],
  );

  /**
   * ⚠️ Has a post been refused in a way the server said a retry would not fix?
   *
   * The card already knows this — `mayPost` withdraws *Post to Atlassian* entirely on a refusal whose
   * `retryable` is not `true` (`checkin_closed`, `checkin_already_posted`, `goal_not_allowlisted` and the
   * rest). The batch has to ask the same question the same way, or it offers to post a key result the
   * screen has already disarmed and then HALTS THE WHOLE RUN on it — and if that one is first in the
   * list, nothing else is posted at all.
   *
   * `retryable !== true` rather than `=== false`, deliberately: `null` means the route did not say, and
   * an unstated opinion is not permission (`lib/api.ts` on the same field).
   */
  const refusedFinally = useCallback(
    (item: ItemView): boolean => {
      const refusal = postRefusals[item.id];
      return refusal !== undefined && refusal.retryable !== true;
    },
    [postRefusals],
  );

  const showsMismatch = useCallback(
    (item: ItemView): boolean => mismatched.has(item.id) || hasMismatch(item),
    [mismatched],
  );

  /**
   * Move the traffic-light, and take the confidence with it.
   *
   * ⚠️ A value stranded outside its new band is RESEEDED to the new midpoint, not merely cleared. Clearing
   * was right while the field started blank; now that it starts seeded, an empty box after changing the
   * light looks like the field broke, and the person is left to guess what leaving it empty will do.
   *
   * A value still INSIDE the new band survives untouched — though the bands do not overlap, so that arm
   * exists for a value the server might one day admit that this screen does not know about, and for the
   * blank a person deliberately made.
   */
  const chooseState = (item: ItemView, next: GoalCheckinState): void => {
    updateAnswer(item, (previous) => {
      const stranded =
        bands !== null && readScoreDecimal(previous.scoreText, bands[next]).outcome === "outside_band";
      return { ...previous, state: next, scoreText: stranded ? seedScoreText(next, bands) : previous.scoreText };
    });
    clearError(item, "score");
  };

  /* -------------------------------------------------------------------------------------------------
   * The writes
   * ----------------------------------------------------------------------------------------------- */

  const draftNow = (): void => {
    if (current === undefined || busy !== null || checkin === null) return;
    const item = current;

    const attempt = buildDraftInput(item, answerFor(item), bands);
    if (!attempt.ok) {
      setErrors((previous) => ({ ...previous, [item.id]: attempt.errors }));
      return;
    }
    setErrors((previous) => ({ ...previous, [item.id]: NO_ERRORS }));

    setBusy(`draft:${item.id}`);
    void draft(checkin.id, item.id, attempt.input)
      .then((updated) => {
        replaceItem(updated);
        setDraftRefusals((previous) => without(previous, item.id));
        setEditing((previous) => withoutMember(previous, item.id));
        // The recorded sentence now answers the recorded answers, so the batch has nothing to re-write.
        setEdited((previous) => withoutMember(previous, item.id));
        // ⚠️ Rule 1: deliberately NOT followed by a post. Nothing here calls `post`.
        toast.show("Preview ready to read", {
          tone: "info",
          detail: "Nothing has been sent yet — read the sentence, then post it.",
        });
      })
      .catch((cause: unknown) => {
        setDraftRefusals((previous) => ({ ...previous, [item.id]: asApiError(cause) }));
      })
      .finally(() => setBusy(null));
  };

  const postNow = (): void => {
    if (current === undefined || busy !== null || checkin === null) return;
    const item = current;
    // ⚠️ Rule 2, restated at the call site rather than trusted to a disabled button. A disabled button is
    // UX; this is the one action in this application that cannot be undone.
    if (blockedFromPosting(item)) return;

    setBusy(`post:${item.id}`);
    void post(checkin.id, item.id)
      .then((result) => {
        replaceItem(result.item);
        setPostRefusals((previous) => without(previous, item.id));
        if (result.mismatch) setMismatched((previous) => withMember(previous, item.id));
        const url = result.url;
        if (url !== null && url !== "") setPostedUrls((previous) => ({ ...previous, [item.id]: url }));
        toast.show(
          result.mismatch ? `${item.key} posted, with a different status recorded` : `${item.key} posted`,
          {
            tone: result.mismatch ? "danger" : "success",
            detail: result.mismatch
              ? "Atlassian recorded a status other than the one intended. Open it and check."
              : `Posted as ${connection?.postedAs ?? "this business unit's Atlassian account"}.`,
          },
        );
      })
      .catch((cause: unknown) => {
        const failure = asApiError(cause);
        setPostRefusals((previous) => ({ ...previous, [item.id]: failure }));
        if (failure.code === "checkin_indeterminate") {
          // The one refusal that ends a key result, recorded before any button can be re-enabled.
          setIndeterminate((previous) => withMember(previous, item.id));
        }
      })
      .finally(() => setBusy(null));
  };

  const skipNow = (): void => {
    if (current === undefined || busy !== null || checkin === null) return;
    const item = current;
    setBusy(`skip:${item.id}`);
    void skip(checkin.id, item.id)
      .then((updated) => {
        replaceItem(updated);
        /*
         * ⚠️ THE SKIP HAS TO TAKE THE TYPING WITH IT, or the batch posts what was just declined.
         *
         * `edited` is sticky — `updateAnswer` adds to it and only a successful draft removes from it —
         * and `awaitingPost` reads it to decide whether a SKIPPED key result is back in play. That clause
         * was written for one order of events (skip, then *Answer it after all*, then type) and the
         * opposite order left the flag standing: type, then skip, and the pager drew a filled dot and
         * *Finish check-in* offered to post a key result whose own card said "Nothing was sent for it".
         *
         * The stored ANSWER stays, deliberately — pressing *Answer it after all* should return the words
         * that were typed. Typing again re-arms the flag, which is exactly the gesture that means it.
         */
        setEdited((previous) => withoutMember(previous, item.id));
        setEditing((previous) => withoutMember(previous, item.id));
        toast.show(`${item.key} skipped`, { tone: "info", detail: "Nothing was sent for it." });
      })
      .catch((cause: unknown) => {
        toast.show("That key result could not be skipped", {
          tone: "danger",
          detail: goalErrorMessage(asApiError(cause)),
        });
      })
      .finally(() => setBusy(null));
  };

  /** Leaving the screen: one place, so the batch and the two direct closes cannot drift apart. */
  const leave = (status: "completed" | "abandoned", detail: string): void => {
    setAbandoning(false);
    setBatch(null);
    toast.show(status === "completed" ? "Check-in finished" : "Check-in abandoned", { tone: "info", detail });
    if (onFinished !== undefined) onFinished();
    else window.location.hash = `#/goals/${encodeURIComponent(goalId)}`;
  };

  const closeNow = (status: "completed" | "abandoned"): void => {
    if (busy !== null || checkin === null) return;
    setBusy(`close:${status}`);
    void closeIt(checkin.id, status)
      .then(() => {
        leave(
          status,
          status === "completed"
            ? "Anything posted stays in Atlassian; anything skipped was never sent."
            : "Nothing further will be posted from it. Anything already posted stays in Atlassian.",
        );
      })
      .catch((cause: unknown) => {
        toast.show("The check-in could not be closed", {
          tone: "danger",
          detail: goalErrorMessage(asApiError(cause)),
        });
      })
      .finally(() => setBusy(null));
  };

  /* -------------------------------------------------------------------------------------------------
   * Finishing — the batch
   * ----------------------------------------------------------------------------------------------- */

  /**
   * Everything answered and not yet posted, in the order the walk holds them.
   *
   * Excludes anything settled (posted, skipped, or the terminal `indeterminate`) and anything blocked
   * under rule 2. The remainder is what *Finish check-in* offers to send.
   */
  /**
   * Everything answered and not yet posted, in the order the walk holds them.
   *
   * ⚠️ **It deliberately INCLUDES a key result the server has finally refused**, and `postable` below is
   * what the run actually writes. Filtering them out here instead was worse in a way that took a second
   * reading to see: with one such key result the only thing outstanding, `pending` was empty, so *Finish
   * check-in* closed the check-in without asking anything at all — and somebody's answered key result was
   * abandoned with no sentence on screen ever mentioning it. Listed and marked *Cannot be posted*, it is
   * at least a fact the person is told.
   */
  const pending = items.filter((entry) => awaitingPost(entry, edited, blockedFromPosting(entry)));
  const postable = pending.filter((entry) => !refusedFinally(entry));

  /** The first thing wrong with any of them, or `null` — so the dialog can refuse before it writes. */
  const pendingProblem = ((): { readonly key: string; readonly message: string } | null => {
    for (const entry of postable) {
      const attempt = buildDraftInput(entry, answerFor(entry), bands);
      if (attempt.ok) continue;
      const message = firstProblem(attempt.errors);
      if (message !== null) return { key: entry.key, message };
    }
    return null;
  })();

  const setStep = (itemId: string, phase: StepPhase, detail: string | null): void => {
    setBatch((previous) =>
      previous === null
        ? null
        : {
            ...previous,
            steps: previous.steps.map((step) => (step.itemId === itemId ? { ...step, phase, detail } : step)),
          },
    );
  };

  /**
   * ⚠️ **The one path on this screen that may post an update nobody has read** — rule 1, as the owner
   * narrowed it on 7 August 2026. It runs only from the confirmation dialog, which lists what it is about
   * to write and says which sentences exist and which do not.
   *
   * **Sequential, and it stops at the first failure.** Both halves are deliberate. Sequential, because a
   * fan-out gives the person no way to say which of five simultaneous writes was the one that went wrong,
   * and because each post is irreversible. Stopping, because the most likely reason a post failed is that
   * the connection has stopped answering — and the most likely SECOND failure is `indeterminate`, which
   * ends a key result for good (rule 2). Carrying on down the list would be firing irreversible writes
   * into a gateway that has just declined to say what happened to the last one. What has landed has
   * landed and is reported as such; the rest is left exactly where it was, and pressing *Finish check-in*
   * again picks up from there, since posted items are no longer pending.
   *
   * A key result is re-drafted first only where it needs to be: nothing recorded, or answers edited since
   * the sentence was written. An unedited draft is posted with the sentence the person previewed, which
   * is the whole reason `edited` is tracked rather than recomputed from the values.
   */
  const runBatch = async (): Promise<void> => {
    const opened = batch;
    // ⚠️ `busy` is in here for the reason every other write on this screen has it, and it is NOT covered
    // by `opened.running`: pressing *Finish without posting* starts a close that leaves this dialog on
    // screen while it is in flight, and a slow close reads as a dead button. The next press would then
    // start a run of irreversible posts INTO a check-in that is being closed.
    if (opened === null || opened.running || checkin === null || busy !== null) return;
    /*
     * ⚠️ A REF, not the state above, and it is not redundant.
     *
     * `opened.running` is read out of the render closure, so it can only refuse a second press that
     * happened after React committed the first one's state. React 18 flushes an event handler's updates
     * before the next event is delivered, so in practice it does — but "in practice it does" is a claim
     * about the scheduler, and what it guards is a loop of writes to somebody else's system that has no
     * delete. The ref is set synchronously, in the same tick as the press, and cannot be raced.
     */
    if (runningRef.current) return;
    runningRef.current = true;

    setBusy("finish");
    setBatch({ ...opened, running: true, halted: false });

    let posted = 0;
    for (const step of opened.steps) {
      const item = items.find((entry) => entry.id === step.itemId);
      if (item === undefined) continue;
      // Listed so the person can see it, and stepped over rather than attempted — trying it would refuse
      // again and halt the whole run on a key result the screen has already said cannot be posted.
      if (step.phase === "cannot") continue;

      /*
       * ⚠️ WHICH CALL FAILED, because the two refusals are kept apart on purpose — "a failed draft must
       * not disarm a post". Filing a draft failure under `postRefusals` (which this did) is wrong twice:
       * the form branch renders `draftRefusals` and would show no banner and no trace id at all, so
       * dropping the walk on that card explains nothing; and `draftNow`'s success clears only
       * `draftRefusals`, so the misfiled entry outlives every later preview and leaves *Post to Atlassian*
       * disabled for the rest of the session, under a red banner about a post that never happened.
       */
      let stage: "writing" | "posting" = "writing";

      try {
        if (!isDrafted(item) || edited.has(item.id)) {
          const attempt = buildDraftInput(item, answerFor(item), bands);
          if (!attempt.ok) {
            // Unreachable from the dialog, which withholds the button while `pendingProblem` stands. Kept
            // because "unreachable" is a claim about today's dialog, and the alternative to checking is a
            // loop that posts to somebody else's system on answers nothing validated.
            setStep(step.itemId, "failed", firstProblem(attempt.errors) ?? "Its answers are not valid.");
            setBatch((previous) => (previous === null ? null : { ...previous, running: false, halted: true }));
            setBusy(null);
            runningRef.current = false;
            return;
          }
          setStep(step.itemId, "writing", null);
          replaceItem(await draft(checkin.id, item.id, attempt.input));
          setEdited((previous) => withoutMember(previous, item.id));
        }

        stage = "posting";
        setStep(step.itemId, "posting", null);
        const result = await post(checkin.id, item.id);
        replaceItem(result.item);
        setPostRefusals((previous) => without(previous, item.id));
        if (result.mismatch) setMismatched((previous) => withMember(previous, item.id));
        const url = result.url;
        if (url !== null && url !== "") setPostedUrls((previous) => ({ ...previous, [item.id]: url }));
        posted += 1;
        setStep(
          step.itemId,
          "posted",
          result.mismatch ? "Atlassian recorded a status other than the one intended. Open it and check." : null,
        );
      } catch (cause) {
        const failure = asApiError(cause);
        if (stage === "writing") {
          setDraftRefusals((previous) => ({ ...previous, [item.id]: failure }));
          // Open the form as well as landing on it: an item that was already drafted would otherwise
          // come back on its PREVIEW, which renders `postRefusals` and would show nothing about the
          // sentence that could not be re-written.
          setEditing((previous) => withMember(previous, item.id));
        } else {
          if (failure.code === "checkin_indeterminate") {
            setIndeterminate((previous) => withMember(previous, item.id));
          }
          setPostRefusals((previous) => ({ ...previous, [item.id]: failure }));
        }
        setStep(step.itemId, "failed", goalErrorMessage(failure));
        setBatch((previous) => (previous === null ? null : { ...previous, running: false, halted: true }));
        // Put the walk on the key result that failed, so *Back to the check-in* lands where the refusal
        // is rather than wherever the cursor happened to be when Finish was pressed. The banner and the
        // trace id are on that card; sending somebody back to a different one leaves them hunting.
        const failedAt = items.findIndex((candidate) => candidate.id === item.id);
        if (failedAt !== -1) setCursor(failedAt);
        setBusy(null);
        runningRef.current = false;
        return;
      }
    }

    try {
      await closeIt(checkin.id, "completed");
    } catch (cause) {
      // Everything asked for landed; only the closing did not. Say so rather than reporting a failure
      // that would read as "your updates did not go" — they did, and they cannot be taken back.
      // `danger` rather than something softer: the toast vocabulary has three tones and the person has to
      // act — the updates are in Atlassian and the check-in is still open, which is the state that would
      // otherwise be discovered by somebody trying to start the next one.
      toast.show("Posted, but the check-in could not be closed", {
        tone: "danger",
        detail: goalErrorMessage(asApiError(cause)),
      });
      setBatch((previous) => (previous === null ? null : { ...previous, running: false, halted: true }));
      setBusy(null);
      runningRef.current = false;
      return;
    }

    setBusy(null);
    runningRef.current = false;
    leave(
      "completed",
      `${posted} key result update(s) posted as ${connection?.postedAs ?? "this business unit's Atlassian account"}.`,
    );
  };

  const finishNow = (): void => {
    if (busy !== null || checkin === null) return;
    if (pending.length === 0) {
      closeNow("completed");
      return;
    }
    setBatch({
      steps: pending.map((entry) => {
        const refusal = postRefusals[entry.id];
        const barred = refusal !== undefined && refusal.retryable !== true;
        return {
          itemId: entry.id,
          key: entry.key,
          name: entry.name,
          phase: (barred ? "cannot" : "waiting") as StepPhase,
          detail: barred && refusal !== undefined ? goalErrorMessage(refusal) : null,
        };
      }),
      running: false,
      halted: false,
    });
  };

  const backLink = (
    <p>
      <Button
        variant="link"
        size="sm"
        onClick={() => (window.location.hash = `#/goals/${encodeURIComponent(goalId)}`)}
      >
        ← Back to the objective
      </Button>
    </p>
  );

  /* -------------------------------------------------------------------------------------------------
   * The states before the walk can be drawn
   * ----------------------------------------------------------------------------------------------- */

  if (session.loading) {
    return (
      <Page title="Check-in">
        <LoadingState label="Reading this objective's key results" />
      </Page>
    );
  }

  if (session.error !== null) {
    const failure = session.error;
    return (
      <Page title="Check-in">
        {backLink}
        {failure.isPermissionDenied ? (
          <DeniedState body={goalErrorMessage(failure)} />
        ) : (
          <ErrorState
            message={goalErrorMessage(failure)}
            traceId={failure.traceId}
            // Opening a check-in writes nothing to Atlassian, so a retry is honest wherever the server
            // has not said otherwise. `retryable: false` — `goals_not_connected`, an archived goal —
            // means pressing it again would only produce the same sentence.
            {...(failure.retryable === false ? {} : { onRetry: session.reload })}
          />
        )}
      </Page>
    );
  }

  if (checkin === null || items.length === 0) {
    return (
      <Page title="Check-in">
        {backLink}
        <ErrorState
          message="This check-in has no key results to walk. In Atlassian, a key result is a child goal of the objective — add one there, then start again."
          onRetry={session.reload}
        />
      </Page>
    );
  }

  const postedCount = items.filter((entry) => entry.state === "posted").length;
  const skippedCount = items.filter((entry) => entry.state === "skipped").length;
  /**
   * Counted and named apart from the other two, because it is the one terminal state that is not a
   * decision anybody made. Folding it into "posted" would claim something nobody knows; folding it into
   * "still to answer" would put a number on screen that no amount of answering could bring down.
   *
   * Read through `blockedFromPosting` rather than off the state, so that the count agrees with the
   * buttons in BOTH cases: a post refused a moment ago has not been re-read, so the copy in hand still
   * says `drafted` while the server's row says `indeterminate`.
   */
  const unknownItems = items.filter((entry) => blockedFromPosting(entry));
  const terminalCount = items.filter((entry) => isSettled(entry) || blockedFromPosting(entry)).length;

  /* -------------------------------------------------------------------------------------------------
   * One key result's body, in the order the branches MUST be taken
   * ----------------------------------------------------------------------------------------------- */

  const renderItemBody = (entry: ItemView): JSX.Element => {
    const postRefusal = postRefusals[entry.id];
    const draftRefusal = draftRefusals[entry.id];

    /* 1. ⚠️ The outcome nobody knows. NO post, NO retry, NO re-draft. Taken first, deliberately: every
     *    branch below this one offers a control that could send a second check-in. */
    if (blockedFromPosting(entry)) {
      return (
        <div className="stack">
          <Banner tone="danger">
            {postRefusal === undefined
              ? "It is not known whether this check-in was posted. Open the key result in Atlassian and look before doing anything else — a goal update cannot be withdrawn, so posting a second one would leave two."
              : goalErrorMessage(postRefusal)}
          </Banner>
          {entry.error === null || entry.error === "" ? null : (
            <p className="field__hint mono">Atlassian said: {entry.error}</p>
          )}
          {postRefusal === undefined || postRefusal.traceId === null || postRefusal.traceId === "" ? null : (
            <p className="field__hint mono">Trace {postRefusal.traceId}</p>
          )}
          <p>
            {entry.url === null ? (
              <span className="field__hint">
                This key result&rsquo;s address in Atlassian could not be read — find it from the objective.
              </span>
            ) : (
              <a href={entry.url} target="_blank" rel="noopener noreferrer">
                Open {entry.key} in Atlassian and check
              </a>
            )}
          </p>
          <p className="field__hint">
            This key result is closed for this check-in. It cannot be posted again from here, and that is
            the point: the first attempt may have landed.
          </p>
          <div className="row">
            <Button variant="secondary" onClick={() => goTo(cursor + 1)} disabled={cursor >= items.length - 1}>
              Move on to the next key result
            </Button>
          </div>
        </div>
      );
    }

    /* 2. Posted. */
    if (entry.state === "posted") {
      const url = postedUrls[entry.id] ?? entry.url;
      return (
        <div className="stack">
          {/* ⚠️ Rule 3 — invisible unless the screen says so. */}
          {showsMismatch(entry) ? (
            <Banner tone="danger">
              Atlassian recorded this as <strong>{goalStateLabel(entry.postedState)}</strong>, not{" "}
              <strong>{goalStateLabel(entry.newState)}</strong> as intended. The update itself was posted;
              open it in Atlassian and correct the status there if it matters.
            </Banner>
          ) : null}
          <p>
            <Badge tone={goalStateTone(entry.postedState ?? entry.newState)}>
              {goalStateLabel(entry.postedState ?? entry.newState)}
            </Badge>{" "}
            {/* The score Atlassian recorded, in the notation its own status chip uses. It is not decoration:
                the score IS the traffic-light here, so it is the number a mismatch is argued about. */}
            {entry.postedScore === null ? null : <>{scoreAsDecimal(entry.postedScore)} </>}
            <time title={formatDateTime(entry.postedAt)}>posted {formatRelative(entry.postedAt)}</time>
            {connection === null ? null : <> as {connection.postedAs}</>}
          </p>
          <p>&ldquo;{entry.summary}&rdquo;</p>
          {/*
            ⚠️ THREE OUTCOMES, NOT ONE. This line used to read `newMetricValue` — the figure that was
            asked for — and that was correct only while the client echoed it back. It is now genuinely
            read out of the update's `metricUpdate` reply, so "asked for" and "recorded" are separate
            facts and `null` is a third: the update landed, the reply said nothing about the reading.
            Reporting the intention as the outcome is the one thing this must not do.
          */}
          {entry.newMetricValue === null ? null : entry.postedMetricValue === null ? (
            <p className="field__hint">
              Progress was sent as {formatMetricValue(entry.newMetricValue, kindOf(entry))}, but Atlassian
              did not confirm the reading. The update itself posted — open it to see where the number stands.
            </p>
          ) : entry.postedMetricValue === entry.newMetricValue ? (
            <p className="field__hint">
              Progress recorded: {formatMetricValue(entry.postedMetricValue, kindOf(entry))}
            </p>
          ) : (
            <p className="field__hint">
              Progress recorded as <strong>{formatMetricValue(entry.postedMetricValue, kindOf(entry))}</strong>
              , not {formatMetricValue(entry.newMetricValue, kindOf(entry))} as entered — Atlassian adjusted
              it.
            </p>
          )}
          {url === null ? null : (
            <p>
              <a href={url} target="_blank" rel="noopener noreferrer">
                Open it in Atlassian
              </a>
            </p>
          )}
        </div>
      );
    }

    const drafted = isDrafted(entry);
    const showForm = editing.has(entry.id) || !drafted;

    /* 3. Skipped — a decision, not an absence. Below the form check so "Answer it after all" works. */
    if (entry.state === "skipped" && !editing.has(entry.id)) {
      return (
        <div className="stack">
          <p>
            <Badge tone="neutral">Skipped</Badge> Nothing was sent for this key result.
          </p>
          <div className="row">
            <Button variant="secondary" onClick={() => setEditing((previous) => withMember(previous, entry.id))}>
              Answer it after all
            </Button>
          </div>
        </div>
      );
    }

    /* 4. The form. */
    if (showForm) {
      const answer = answerFor(entry);
      const problem = errors[entry.id] ?? NO_ERRORS;
      // Only the VERBATIM reading is counted — notes are bounded by the server's safety limit alone.
      const tooLong = answer.wordsMode === "verbatim" && answer.words.trim().length > SUMMARY_MAX_CHARS;
      return (
        <div className="stack">
          {draftRefusal === undefined ? null : (
            <>
              <Banner tone="danger">{goalErrorMessage(draftRefusal)}</Banner>
              {draftRefusal.traceId === null || draftRefusal.traceId === "" ? null : (
                <p className="field__hint mono">Trace {draftRefusal.traceId}</p>
              )}
            </>
          )}

          <RadioGroup
            legend="How is this key result going?"
            value={answer.state}
            options={CHECKIN_STATE_OPTIONS.map((option) => ({
              value: option.value,
              label: option.label,
              hint: option.hint,
            }))}
            onChange={(value) => chooseState(entry, value as GoalCheckinState)}
            hint="Atlassian stores this as a score, so it is set rather than described."
          />

          {/*
            The confidence — the same score, said precisely. The light above chooses the band; this says
            where inside it, and Atlassian prints the two together in its own status chip ("At risk 0.5").

            `inputMode="decimal"` and not `numeric`: on iOS `numeric` raises a digits-only keypad with no
            decimal point, and every valid value here has one. The kit gained `decimal` for this field.

            Seeded with the band's midpoint rather than left blank — the value that would have been
            applied anyway, so the field states the default instead of hiding it in the hint.
          */}
          {bands === null ? null : (
            <TextField
              label="Confidence"
              value={answer.scoreText}
              onChange={(value) => updateAnswer(entry, (previous) => ({ ...previous, scoreText: value }))}
              inputMode="decimal"
              width="short"
              hint={scoreBandRange(answer.state, bands[answer.state])}
              {...(problem.score === undefined ? {} : { error: problem.score })}
            />
          )}

          {/*
            ⚠️ Rule 5 — ONE FIELD, SEEDED WITH THE READING AS IT STANDS, on the owner's instruction of
            7 August 2026. It replaced a *Keep it at 20%* / *Set a new value* pair, which solved the right
            problem the long way round: the pair existed so that a blank box could not be mistaken for a
            decision, and a box that is never blank cannot be mistaken for one either. Leaving the number
            alone is now the default in the most literal sense — it is already typed.

            The unit rides in the LABEL rather than as an adornment on the control: the kit has no suffix
            slot, and inventing one here would be a component this screen owned privately. The VALUE is
            the raw number for the reason `seedMetricText` gives — what is in the box is parsed straight
            back out of it.
          */}
          {entry.metric === null ? null : (
            <TextField
              label={`The reading for ${orNotRead(entry.metric.name)}${kindOf(entry) === "percentage" ? " (%)" : ""}`}
              value={answer.metricText}
              onChange={(value) => updateAnswer(entry, (previous) => ({ ...previous, metricText: value }))}
              inputMode="decimal"
              width="short"
              {...(problem.metric === undefined ? {} : { error: problem.metric })}
            />
          )}

          {/*
            ONE box, and a radio saying what is in it. `TextArea` rather than `TextField` because both
            readings are prose: a single-line box against even the 280 scrolls sideways, and re-reading
            what you wrote means arrowing through it.

            No help text under either control, deliberately — the labels carry the whole distinction and a
            paragraph explaining it was what made this the longest part of the form.
          */}
          <RadioGroup
            legend="What you write"
            value={answer.wordsMode}
            options={[
              { value: "notes", label: "Notes — the model writes the update from them" },
              { value: "verbatim", label: "The update itself — post it exactly as written" },
            ]}
            onChange={(value) => {
              updateAnswer(entry, (previous) => ({
                ...previous,
                wordsMode: value === "verbatim" ? "verbatim" : "notes",
              }));
              // The bound changes with the choice, so a refusal earned under the old one must not outlive
              // it: switching back to notes makes an over-long value legal again, and an error still
              // standing under a legal field is a form that cannot be submitted for a reason it will not
              // show you. The reverse case — notes too long to post verbatim — is caught on Preview.
              clearError(entry, "words");
            }}
          />

          <TextArea
            label={answer.wordsMode === "verbatim" ? "The update" : "Notes"}
            value={answer.words}
            onChange={(value) => updateAnswer(entry, (previous) => ({ ...previous, words: value }))}
            rows={answer.wordsMode === "verbatim" ? 3 : 5}
            /* ⚠️ The bound follows the mode. Verbatim text IS the check-in and Atlassian refuses it past
               280; notes are raw material the model writes from, and rationing them would ration the
               context that makes the sentence good. `NOTE_MAX_CHARS` is the server's safety bound, not a
               length anybody meets, and is passed only so the browser stops rather than the round trip. */
            maxLength={answer.wordsMode === "verbatim" ? SUMMARY_MAX_CHARS : NOTE_MAX_CHARS}
            {...(problem.words === undefined ? {} : { error: problem.words })}
          />

          <div className="row">
            <Button
              variant="primary"
              onClick={draftNow}
              loading={busy === `draft:${entry.id}`}
              disabled={busy !== null || tooLong}
              title="Write the sentence and show it. Nothing is sent to Atlassian by this."
            >
              Preview update
            </Button>
            <Button
              variant="ghost"
              onClick={skipNow}
              loading={busy === `skip:${entry.id}`}
              disabled={busy !== null}
            >
              Skip this key result
            </Button>
          </div>
          <p className="field__hint">
            Previewing sends nothing. Your answers are kept as you move through the key results, and
            nothing reaches Atlassian until you post it or finish the check-in.
          </p>
        </div>
      );
    }

    /* 5. The preview — rule 1. This is the ONLY per-key-result branch from which a post can be made. */
    const retryable = postRefusal !== undefined && postRefusal.retryable === true;
    // The same reading the batch takes, through the same function — see `refusedFinally`.
    const mayPost = !refusedFinally(entry);

    return (
      <div className="stack">
        {postRefusal === undefined ? null : (
          <>
            <Banner tone="danger">{goalErrorMessage(postRefusal)}</Banner>
            {postRefusal.traceId === null || postRefusal.traceId === "" ? null : (
              <p className="field__hint mono">Trace {postRefusal.traceId}</p>
            )}
          </>
        )}

        {/*
          ⚠️ Rule 1's "exact figure" now includes the confidence. The score is not a refinement of the
          traffic-light on this gateway — it IS the traffic-light, and it is the number the person just
          chose, so approving a check-in without seeing it would be approving the wrong half of it.
        */}
        <p>
          <strong>Status to post</strong>{" "}
          <Badge tone={goalStateTone(entry.newState)}>{goalStateLabel(entry.newState)}</Badge>
          {entry.newScore === null ? null : ` ${scoreAsDecimal(entry.newScore)}`}
        </p>
        {/*
          Written as the transition Atlassian itself renders on a posted update — "Progress 0% → 20%" —
          rather than as the new figure alone. The reading is being OVERWRITTEN, and a person approving
          that should see what is being written over; "20%" on its own is only half the sentence.
        */}
        <p>
          <strong>Progress</strong> {progressLine(entry)}
        </p>
        <p>
          <strong>The sentence, exactly as it will be posted</strong>
        </p>
        <p>&ldquo;{entry.summary}&rdquo;</p>
        <p className="field__hint">
          {entry.summarySource === "user"
            ? "Your own words, used verbatim."
            : "Written by the model from your answers."}{" "}
          {connection === null
            ? "There is no Atlassian connection, so this cannot be posted."
            : `It will appear in Atlassian under ${connection.postedAs}.`}
        </p>

        <div className="row">
          <Button
            variant="primary"
            onClick={postNow}
            loading={busy === `post:${entry.id}`}
            disabled={busy !== null || !mayPost || connection === null}
            title="Send this check-in to Atlassian. A goal update cannot be edited or withdrawn afterwards."
          >
            {retryable ? "Try again" : "Post to Atlassian"}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setEditing((previous) => withMember(previous, entry.id))}
            disabled={busy !== null}
          >
            Edit the answers
          </Button>
          <Button
            variant="ghost"
            onClick={skipNow}
            loading={busy === `skip:${entry.id}`}
            disabled={busy !== null}
          >
            Skip this key result
          </Button>
        </div>
      </div>
    );
  };

  /* -------------------------------------------------------------------------------------------------
   * The batch's own reading of a key result — the "exact status, exact figure" half of rule 1
   * ----------------------------------------------------------------------------------------------- */

  /**
   * What the batch will write for one key result, as far as it is known before it writes it.
   *
   * ⚠️ Read from the ANSWERS the person holds, not from the item's recorded columns, because for
   * everything except an untouched draft those columns are one edit out of date. The confidence is
   * resolved the way `buildDraftInput` resolves it — a blank box means the band's midpoint, and printing
   * an empty string there would be the dialog declining to say what it is about to post.
   */
  const intendedLine = (entry: ItemView): string => {
    const answer = answerFor(entry);
    const band = bands?.[answer.state];
    const reading = band === undefined ? { outcome: "blank" as const } : readScoreDecimal(answer.scoreText, band);
    const score =
      reading.outcome === "score"
        ? scoreAsDecimal(reading.score)
        : band === undefined
          ? null
          : scoreAsDecimal(band.midpoint);
    const typed = answer.metricText.trim();
    const movement =
      entry.metric === null
        ? "tracks no number"
        : typed === "" || typed === seedMetricText(entry)
          ? `${formatMetricValue(entry.metric.current, kindOf(entry))} (unchanged)`
          : `${formatMetricValue(entry.metric.current, kindOf(entry))} → ${formatMetricValue(Number(typed), kindOf(entry))}`;
    return `${goalStateLabel(answer.state)}${score === null ? "" : ` ${score}`} · ${movement}`;
  };

  /**
   * ⚠️ **The half of rule 1 the batch genuinely gives up, said out loud.**
   *
   * A key result that is drafted and unedited will post the sentence on screen; anything else has no
   * sentence yet, and the model writes one during the run. The dialog must never imply otherwise — a
   * confirmation that quietly claims every update has been read is worse than no confirmation, because
   * the person stops looking.
   */
  const sentenceLine = (entry: ItemView): string => {
    // What was previewed is what will post, whoever wrote it.
    if (isDrafted(entry) && !edited.has(entry.id)) return `“${entry.summary ?? ""}”`;
    // ⚠️ A sentence the person typed under *The update itself* travels as `summary` and is posted
    // VERBATIM — no model call is made for it at all. Saying "the model will write the sentence" here
    // was wrong twice over: it named the wrong author, and it withheld the exact text about to be
    // published in the one case where that text is already known. This is the branch in which rule 1
    // can still be honoured in full, so it is.
    const answer = answerFor(entry);
    const said = answer.words.trim();
    if (answer.wordsMode === "verbatim" && said !== "") return `“${said}” — your own words, posted as written.`;
    return "The model will write the sentence when this posts — it has not been read.";
  };

  /* -------------------------------------------------------------------------------------------------
   * The walk
   * ----------------------------------------------------------------------------------------------- */

  const activeStep =
    batch === null ? undefined : batch.steps.find((step) => step.phase === "writing" || step.phase === "posting");

  /**
   * What a screen reader is told about the run, in ONE region that is mounted for the whole life of the
   * dialog.
   *
   * ⚠️ The progress used to be a live region inside the `running` branch alone — so the moment a run
   * HALTED, that branch was replaced and the region was unmounted along with it. `Banner` is a plain
   * paragraph with no `role="alert"`, so the failure that stopped four irreversible writes was announced
   * to nobody. A live region removed at the moment it has something to say is worse than none: it reads
   * as working right up until it matters.
   */
  const liveMessage = ((): string => {
    if (batch === null) return "";
    if (batch.halted) {
      const failed = batch.steps.find((step) => step.phase === "failed");
      const landed = batch.steps.filter((step) => step.phase === "posted").length;
      return failed === undefined
        ? "The run stopped. Nothing further was sent."
        : `The run stopped at ${failed.key}: ${failed.detail ?? "it was not posted"}. ${landed} update(s) were posted before it and cannot be withdrawn.`;
    }
    if (!running) return "";
    if (activeStep === undefined) return "Finishing the check-in.";
    return `${phaseWord(activeStep.phase)} ${activeStep.key} — ${batch.steps.filter((step) => step.phase === "posted").length + 1} of ${batch.steps.length}.`;
  })();

  return (
    <Page
      title="Check-in"
      lede={`${items.length} key result(s) · ${postedCount} posted · ${skippedCount} skipped${unknownItems.length === 0 ? "" : ` · ${unknownItems.length} outcome unknown`} · ${items.length - terminalCount} still to answer`}
    >
      {backLink}

      {/*
        ⚠️ The one thing on this screen that must not depend on where the cursor happens to be.
        An indeterminate key result is terminal, so the walk opens PAST it — and a warning only visible
        on the card of an item the walk skips is a warning nobody reads. It is raised at page level,
        naming each key result, for as long as the check-in is open.
      */}
      {unknownItems.length === 0 ? null : (
        <Banner tone="danger">
          It is not known whether {unknownItems.map((entry) => entry.key).join(", ")} was checked in. Open{" "}
          {unknownItems.length === 1 ? "it" : "each of them"} in Atlassian and look before starting
          another check-in — a goal update cannot be withdrawn, so posting a second would leave two.
        </Banner>
      )}

      {/*
        The standing banner naming the posting account was REMOVED on the owner's instruction of
        7 August 2026. It is worth saying what remains, because rule 4 is not withdrawn: the account is
        still named on every posted key result ("posted 2 minutes ago as ticketless@centro.team"), in the
        toast that confirms each post, and in the confirmation the batch shows before it writes anything.
        What went was the paragraph repeating it above every screen, before anything had been posted, on
        every render — the identity is a fact about a post, and it is now stated where posts are rather
        than where they are not yet.

        The `connection === null` case is NOT part of that and stays: it is the only thing on this screen
        that says the business unit cannot post at all, and a check-in flow that silently offers a preview
        it can never send is the failure the whole screen would then be.
      */}
      {connection === null ? (
        <Banner tone="warning">
          This business unit is not connected to Atlassian Goals, so nothing can be posted from here.
        </Banner>
      ) : null}

      {data?.resumed === true ? (
        <Banner tone="info">
          Resuming the check-in started {formatRelative(checkin.startedAt)} by{" "}
          {orNotRead(checkin.actorEmail)}. Key results already posted are shown as they were sent.
        </Banner>
      ) : null}

      {/*
        The whole walk at a glance, and the way to move about it out of order.

        ⚠️ THE LEGEND IS NOT DECORATION. This was a bare row of numerals with no visible statement of what
        they counted — a reader arriving at the screen saw "1 2 3 4 5 6 / 7" and had to infer it.
        `03-components.md` §11 asks a pager to show "the current position and the total where the total is
        known"; both were known here and neither was shown, the card's own heading being below the fold on
        a phone. The legend is also the strip's accessible name, via `aria-labelledby`, so the sighted
        reader and the screen-reader user are told the same thing rather than two different things.

        ⚠️ **The mark under each numeral distinguishes ANSWERED from POSTED**, on the owner's instruction
        of 7 August 2026: a hollow dot is a key result nobody has touched, a filled one has answers waiting
        to go, and a tick is in Atlassian and cannot be taken back. It shipped with one shade for both,
        which told a person that eleven key results were done when four had been written anywhere. The
        precise word is still in the accessible name, where a reader who wants it can get it — and the
        legend says what the marks mean, since `03-components.md` §5.2 rules out a tooltip as the sole
        carrier on a screen whose whole reason for existing is to be worked through on a phone.
      */}
      <nav className="checkin-pager" aria-labelledby="checkin-pager-legend">
        <p className="checkin-pager__legend" id="checkin-pager-legend">
          Key results — <strong>{cursor + 1} of {items.length}</strong>. Press a number to jump to one: a
          filled dot has answers waiting, a tick has been posted, a square needs another look.
        </p>
        {/* Scrolls rather than wraps: seven 44px targets do not fit a narrow phone, and the alternative
            §11 forbids is shrinking them. `ScrollStrip` draws its direction marks only when the row
            actually overflows (§8.1), so a short check-in gets nothing. */}
        <ScrollStrip currentKey={current?.id ?? null}>
          <ul className="checkin-pager__steps">
            {items.map((entry, index) => {
              const blocked = blockedFromPosting(entry);
              const word = stateWord(entry, blocked);
              const mark = pagerMark(
                entry,
                blocked,
                awaitingPost(entry, edited, blocked),
                postRefusals[entry.id] !== undefined,
              );
              return (
                <li key={entry.id}>
                  <button
                    type="button"
                    className="checkin-pager__step"
                    data-mark={mark}
                    aria-current={index === cursor ? "step" : undefined}
                    aria-label={`Key result ${index + 1} of ${items.length}: ${entry.name} — ${word}`}
                    onClick={() => goTo(index)}
                  >
                    <span>{index + 1}</span>
                    {mark === "posted" ? <Tick /> : <span className="checkin-pager__dot" aria-hidden="true" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </ScrollStrip>
      </nav>

      {current === undefined ? null : (
        <Card
          id={CURRENT_CARD_ID}
          title={`Key result ${cursor + 1} of ${items.length} — ${current.name}`}
          support={
            <>
              {current.url === null ? (
                <>{current.key} · its address in Atlassian could not be read</>
              ) : (
                <a href={current.url} target="_blank" rel="noopener noreferrer">
                  {current.key} in Atlassian
                </a>
              )}
              {" · currently "}
              {goalStateLabel(current.currentState)}
            </>
          }
          actions={
            <Badge tone={stateTone(current, blockedFromPosting(current))}>
              {stateWord(current, blockedFromPosting(current))}
            </Badge>
          }
        >
          <div className="stack">
            {/* §5.3 R2. A key result with no metric genuinely tracks no number — that is a fact about it,
                where a blank would read as a figure we had failed to fetch. */}
            {current.metric === null ? (
              <p className="field__hint">
                This key result tracks no number, so its check-in is a status and a sentence.
              </p>
            ) : (
              <p className="field__hint">
                {/* `type` is the opaque string the gateway gave us and stays on screen beside the figures
                    it governs: when a metric formats as a plain number because its type was not one this
                    application recognises, this line is the only place that says so. */}
                <strong>{orNotRead(current.metric.name)}</strong> ({orNotRead(current.metric.type)}) —{" "}
                {describeMetric(current.metric, kindOf(current))}
              </p>
            )}

            {renderItemBody(current)}
          </div>
        </Card>
      )}

      <div className="row">
        <Button variant="secondary" onClick={() => goTo(cursor - 1)} disabled={cursor === 0}>
          ← Previous
        </Button>
        <Button variant="secondary" onClick={() => goTo(cursor + 1)} disabled={cursor >= items.length - 1}>
          Next key result →
        </Button>
      </div>

      <FormActions>
        <Button
          variant="ghost"
          onClick={() => setAbandoning(true)}
          disabled={busy !== null}
          title="Close this check-in without posting anything further."
        >
          Abandon check-in
        </Button>
        <Button
          variant="primary"
          onClick={finishNow}
          loading={busy === "close:completed"}
          disabled={busy !== null}
          title={
            pending.length > 0
              ? `${pending.length} key result(s) have answers that have not been posted — you will be asked about them.`
              : terminalCount === items.length
                ? "Close this check-in."
                : `${items.length - terminalCount} key result(s) have not been answered — they will simply be left.`
          }
        >
          Finish check-in
        </Button>
      </FormActions>

      {/*
        ⚠️ Rule 1 as the owner narrowed it. Three things this dialog must keep doing:
        it NAMES every key result it is about to write, with the status and figure it will write; it says
        of each sentence whether it exists or will be written during the post; and it offers finishing
        WITHOUT posting, because a person who opened it to see what was outstanding must be able to leave
        without sending anything.
      */}
      <Modal
        open={batch !== null}
        role="alertdialog"
        title={
          batch !== null && batch.halted
            ? "The run stopped"
            : running
              ? "Posting to Atlassian"
              : `Post ${postable.length} key result update(s) and finish?`
        }
        onClose={() => setBatch(null)}
        // While it is writing, neither Escape nor the scrim may dismiss it: the run does not stop because
        // the dialog closed, and a hidden run is one whose outcome nobody sees.
        confirmDismiss={() => !running}
        actions={
          batch !== null && batch.halted ? (
            <Button variant="secondary" onClick={() => setBatch(null)}>
              Back to the check-in
            </Button>
          ) : running ? (
            <Button variant="ghost" disabled>
              Posting…
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setBatch(null)} disabled={busy !== null}>
                Keep going
              </Button>
              {/* The close it starts leaves this dialog on screen, so it must show that it is working —
                  a button that looks idle for three seconds is a button somebody presses again. */}
              <Button
                variant="secondary"
                onClick={() => closeNow("completed")}
                loading={busy === "close:completed"}
                disabled={busy !== null}
              >
                Finish without posting
              </Button>
              <Button
                variant="primary"
                onClick={() => void runBatch()}
                disabled={busy !== null || connection === null || pendingProblem !== null || postable.length === 0}
                title="Write any sentence that is missing, post every update below, and close the check-in."
              >
                Post {postable.length} and finish
              </Button>
            </>
          )
        }
      >
        <div className="stack">
          {/* Mounted whatever the dialog is showing — see `liveMessage`. Visually hidden because every
              word of it is already on screen; what it carries is the CHANGE, to a reader who cannot see
              the dialog rewrite itself. */}
          <p className="visually-hidden" aria-live="polite">
            {liveMessage}
          </p>
          {batch === null ? null : batch.halted ? (
            <Banner tone="danger">
              Nothing further was sent. What is marked posted below is in Atlassian and cannot be
              withdrawn; everything else is exactly as you left it. Read the reason, then finish the
              check-in again to pick up from there.
            </Banner>
          ) : running ? (
            // The spinner sits BESIDE the live region rather than inside it: `Spinner` carries
            // `role="status"`, which is itself a polite live region, and nesting one inside another is
            // how a screen reader ends up announcing each step twice.
            /*
              ⚠️ `tabIndex={0}` IS LOAD-BEARING, and it is not a stray tab stop.
              When the run starts, the three action buttons are unmounted and replaced by one DISABLED
              button — and `Modal`'s focus trap selects `button:not([disabled])`, so the dialog then held
              ZERO focusable elements. Focus fell to `<body>`, and the trap's own guard (`if
              (focusable.length === 0) return`) let Tab walk straight out into the page behind the scrim.
              This paragraph is what the trap holds on to, and the effect below puts focus on it, so the
              first thing a keyboard user meets after pressing the button is the sentence saying what is
              happening.
            */
            <div className="row">
              <Spinner label="Posting" />
              <p tabIndex={0} ref={progressRef}>
                {activeStep === undefined
                  ? "Finishing…"
                  : `${phaseWord(activeStep.phase)} ${activeStep.key} — ${batch.steps.filter((step) => step.phase === "posted").length + 1} of ${batch.steps.length}.`}
              </p>
            </div>
          ) : (
            <>
              <p>
                These will be posted to Atlassian, in order, under{" "}
                <strong>{connection?.postedAs ?? "this business unit's Atlassian account"}</strong>. A goal
                update cannot be edited or withdrawn afterwards.
              </p>
              {pendingProblem === null ? null : (
                <Banner tone="danger">
                  {pendingProblem.key} cannot be posted as it stands: {pendingProblem.message} Open it and
                  correct it, then finish again.
                </Banner>
              )}
              {/*
                The page's own "not connected" warning is behind the scrim at this point, so a person
                looking at a disabled primary action would have no way of finding out why it is disabled
                — and a disabled control with no reason reads as the screen being broken.
              */}
              {connection !== null ? null : (
                <Banner tone="warning">
                  This business unit is not connected to Atlassian Goals, so none of these can be posted.
                  You can still finish the check-in; the answers stay here, unposted.
                </Banner>
              )}
            </>
          )}

          <ul className="checkin-batch">
            {(batch?.steps ?? []).map((step) => {
              const entry = items.find((candidate) => candidate.id === step.itemId);
              return (
                <li key={step.itemId} className="checkin-batch__step" data-phase={step.phase}>
                  {/*
                    ⚠️ THE SAME MARK THE PAGER DRAWS, repeated here on purpose. The pager does tick each
                    key result off as its post returns — that is where the owner asked for it, and it is
                    what a person sees the moment this dialog closes. But it cannot be WATCHED there:
                    the scrim sits over it at 55% (70% in dark), and `Modal` sets `overflow: hidden` on
                    the body while it is open, so a pager that was scrolled out of view when *Finish
                    check-in* was pressed — which it is, since that button lives at the foot of a long
                    card — cannot be scrolled back to. So the mark is drawn where the person is actually
                    looking, in the same shape, and the phase word beneath it carries the same fact for a
                    reader who can see neither.
                  */}
                  <p className="checkin-batch__head">
                    {step.phase === "posted" ? <Tick /> : <span className="checkin-pager__dot" aria-hidden="true" />}
                    <span>
                      <strong>{step.key}</strong> — {step.name}
                    </span>
                  </p>
                  {entry === undefined ? null : (
                    <>
                      <p className="field__hint">{intendedLine(entry)}</p>
                      <p className="field__hint">{sentenceLine(entry)}</p>
                    </>
                  )}
                  {step.phase === "waiting" && !running ? null : (
                    <p className="field__hint">{phaseWord(step.phase)}</p>
                  )}
                  {step.detail === null ? null : <p className="field__hint">{step.detail}</p>}
                </li>
              );
            })}
          </ul>
        </div>
      </Modal>

      <Modal
        open={abandoning}
        role="alertdialog"
        title="Abandon this check-in?"
        onClose={() => setAbandoning(false)}
        actions={
          <>
            <Button variant="ghost" onClick={() => setAbandoning(false)}>
              Keep going
            </Button>
            <Button
              variant="danger"
              onClick={() => closeNow("abandoned")}
              loading={busy === "close:abandoned"}
            >
              Abandon
            </Button>
          </>
        }
      >
        <p>
          Nothing further will be posted from this check-in, and a new one can be started at any time.{" "}
          <strong>Anything already posted stays in Atlassian</strong> — a goal update cannot be withdrawn.
        </p>
      </Modal>
    </Page>
  );
}
